/*
 * cart-items.js — cluster "cart-page"
 * ---------------------------------------------------------------------------
 * Defines the two custom elements that the cart page (and, through the
 * <cart-drawer-items> subclass owned by the cart-drawer cluster, the drawer)
 * needs:
 *
 *   <cart-items>      sections/main-cart-items.liquid:15  (only render site)
 *   <quantity-input>  sections/main-cart-items.liquid:226
 *                     snippets/cart-drawer.liquid:331
 *                     sections/main-product.liquid:383
 *
 * Reimplemented from Shopify Dawn (MIT) — assets/cart.js `CartItems` and
 * assets/global.js `QuantityInput` — adapted to the exact DOM this theme
 * renders. No Liquid was changed; every selector below is quoted to the file
 * and line it was read from.
 *
 * IMPORTANT — shared helpers.
 * `debounce`, `trapFocus`, `removeTrapFocus`, `fetchConfig`, `publish`,
 * `subscribe`, `PUB_SUB_EVENTS` and `ON_CHANGE_DEBOUNCE_TIMER` belong to the
 * shared/global module, NOT to this cluster. This file never defines them at
 * global scope. It reads them through the small `themeXxx()` accessors below,
 * which fall back to a private local equivalent (clearly non-colliding names)
 * so that this file still works if it happens to load before that module.
 *
 * IMPORTANT — fetch.
 * snippets/free-gifts.liquid:915-928 monkey-patches `window.fetch` and matches
 * CART_WRITE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/i
 * (snippets/free-gifts.liquid:494) to re-sync the free-gift lines. Every cart
 * request here therefore calls the live global `fetch(...)` — never a captured
 * alias, never XHR.
 *
 * Plain ES2019. No optional chaining, no class fields, no imports.
 */
(function () {
  'use strict';

  /* ---------------------------------------------------------------------- *
   * Shared-helper accessors (never define the shared names themselves)
   * ---------------------------------------------------------------------- */

  /* Dawn's ON_CHANGE_DEBOUNCE_TIMER. 300 is the upstream value and is used
     only when the shared module has not defined the constant yet. */
  var CART_ITEMS_FALLBACK_DEBOUNCE_MS = 300;

  /* Dawn pubsub.js event names — used verbatim as the fallback so that a
     later-loading shared module publishing the same strings still matches. */
  var CART_ITEMS_FALLBACK_PUB_SUB_EVENTS = {
    cartUpdate: 'cart-update',
    quantityUpdate: 'quantity-update',
    variantChange: 'variant-change',
    cartError: 'cart-error'
  };

  /* Locally-named debounce, used only if the theme-wide `debounce` (consumed by
     assets/secondary.js and assets/media-gallery.js:17) is not available yet. */
  function cartItemsLocalDebounce(fn, wait) {
    var timer;
    return function () {
      var context = this;
      var args = arguments;
      clearTimeout(timer);
      timer = setTimeout(function () {
        fn.apply(context, args);
      }, wait);
    };
  }

  function themeDebounce(fn, wait) {
    try {
      if (typeof debounce === 'function') return debounce(fn, wait);
    } catch (e) {
      /* identifier not declared yet — fall through */
    }
    return cartItemsLocalDebounce(fn, wait);
  }

  function themeOnChangeDebounceTimer() {
    try {
      if (typeof ON_CHANGE_DEBOUNCE_TIMER === 'number') return ON_CHANGE_DEBOUNCE_TIMER;
    } catch (e) {
      /* not declared yet */
    }
    return CART_ITEMS_FALLBACK_DEBOUNCE_MS;
  }

  /* Returns true when focus was actually trapped, so callers can fall back to
     a plain .focus(). Never throws if the shared trapFocus is missing. */
  function themeTrapFocus(container, elementToFocus) {
    if (!container) return false;
    try {
      if (typeof trapFocus === 'function') {
        trapFocus(container, elementToFocus);
        return true;
      }
    } catch (e) {
      /* not declared yet */
    }
    return false;
  }

  function themeFetchConfig(type) {
    var accept = type || 'json';
    try {
      if (typeof fetchConfig === 'function') return fetchConfig(accept);
    } catch (e) {
      /* not declared yet */
    }
    /* Dawn's fetchConfig, inlined as a private fallback only. */
    return {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/' + accept
      }
    };
  }

  function themePubSubEvents() {
    try {
      if (typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS) return PUB_SUB_EVENTS;
    } catch (e) {
      /* not declared yet */
    }
    if (window.PUB_SUB_EVENTS) return window.PUB_SUB_EVENTS;
    return CART_ITEMS_FALLBACK_PUB_SUB_EVENTS;
  }

  function themePublish(eventName, data) {
    if (!eventName) return;
    try {
      if (typeof publish === 'function') {
        publish(eventName, data);
        return;
      }
    } catch (e) {
      /* not declared yet */
    }
    if (typeof window.publish === 'function') {
      try {
        window.publish(eventName, data);
      } catch (e) {
        /* never let a subscriber break the cart */
      }
    }
  }

  /* Returns an unsubscribe function, or undefined when `subscribe` is absent. */
  function themeSubscribe(eventName, callback) {
    if (!eventName) return undefined;
    try {
      if (typeof subscribe === 'function') return subscribe(eventName, callback);
    } catch (e) {
      /* not declared yet */
    }
    if (typeof window.subscribe === 'function') {
      try {
        return window.subscribe(eventName, callback);
      } catch (e) {
        return undefined;
      }
    }
    return undefined;
  }

  /*
   * Subscribe now if the shared pubsub module is already loaded; otherwise try
   * once more when the document is ready. Script order between this file and
   * the shared module is not guaranteed, and a missed subscription would leave
   * the cart page and the drawer permanently out of sync.
   */
  function themeSubscribeWhenReady(element, eventName, callback, store) {
    var unsubscriber = themeSubscribe(eventName, callback);
    if (unsubscriber) {
      store(unsubscriber);
      return;
    }
    var retry = function () {
      if (!element.isConnected) return;
      var later = themeSubscribe(eventName, callback);
      if (later) store(later);
    };
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', retry, { once: true });
    } else {
      setTimeout(retry, 0);
    }
  }

  /* window.routes / window.cartStrings — layout/theme.liquid:672-684. */
  function themeRoutes() {
    return window.routes || {};
  }

  function themeCartStrings() {
    return window.cartStrings || { error: '', quantityError: '' };
  }

  function toArray(nodeList) {
    return Array.prototype.slice.call(nodeList || []);
  }

  /* ---------------------------------------------------------------------- *
   * <cart-items>  — Dawn assets/cart.js `CartItems`
   * ---------------------------------------------------------------------- */

  var CartItems = function () {};

  if (typeof HTMLElement === 'function') {
    CartItems = class CartItems extends HTMLElement {
      constructor() {
        super();

        this.cartUpdateUnsubscriber = undefined;
        this.lineItemStatusElementOverride = null;

        /* Delegated, debounced `change`. The bubbling `change` comes either
           from the native input edit or from the plain Event that
           <quantity-input> re-dispatches on +/- clicks. Dawn:
           addEventListener('change', debounce(this.onChange.bind(this), 300)) */
        var self = this;
        this.debouncedOnChange = themeDebounce(function (event) {
          self.onChange(event);
        }, themeOnChangeDebounceTimer());

        this.addEventListener('change', this.debouncedOnChange);
      }

      /*
       * `#shopping-cart-line-item-status` — sections/main-cart-items.liquid:352
       * `#CartDrawer-LineItemStatus`      — snippets/cart-drawer.liquid:474
       *
       * Resolved lazily rather than cached in the constructor: in the drawer
       * this <p> lives INSIDE `#CartDrawer-CartItems` (the `.js-contents`
       * element, snippets/cart-drawer.liquid:136), so a cached reference goes
       * stale on every innerHTML re-render.
       */
      get lineItemStatusElement() {
        if (this.lineItemStatusElementOverride && this.lineItemStatusElementOverride.isConnected) {
          return this.lineItemStatusElementOverride;
        }
        return (
          document.getElementById('shopping-cart-line-item-status') ||
          document.getElementById('CartDrawer-LineItemStatus')
        );
      }

      /* Kept so a subclass written against Dawn (which assigns this property in
         its constructor) does not throw on a getter-only accessor. */
      set lineItemStatusElement(element) {
        this.lineItemStatusElementOverride = element || null;
      }

      connectedCallback() {
        var self = this;
        var events = themePubSubEvents();
        themeSubscribeWhenReady(
          this,
          events.cartUpdate,
          function (event) {
            try {
              /* Ignore our own publish, exactly as Dawn does. */
              if (event && event.source === 'cart-items') return;
              self.onCartUpdate();
            } catch (e) {
              console.error('[cart-items] cartUpdate handler failed', e);
            }
          },
          function (unsubscriber) {
            self.cartUpdateUnsubscriber = unsubscriber;
          }
        );
      }

      disconnectedCallback() {
        if (typeof this.cartUpdateUnsubscriber === 'function') {
          try {
            this.cartUpdateUnsubscriber();
          } catch (e) {
            /* no-op */
          }
          this.cartUpdateUnsubscriber = undefined;
        }
      }

      /*
       * Section Rendering API entries.
       *
       * The section *instance ids* in this theme are `cart-items` and
       * `cart-footer` (templates/cart.json), NOT upstream Dawn's
       * `main-cart-items` / `main-cart-footer`. They are therefore read off the
       * markup — `#main-cart-items[data-id]` (sections/main-cart-items.liquid:44)
       * and `#main-cart-footer[data-id]` (sections/main-cart-footer.liquid:1) —
       * and never hardcoded.
       */
      getSectionsToRender() {
        var sections = [];

        var mainCartItems = document.getElementById('main-cart-items');
        if (mainCartItems && mainCartItems.dataset.id) {
          sections.push({
            id: 'main-cart-items',
            section: mainCartItems.dataset.id,
            selector: '.js-contents' /* sections/main-cart-items.liquid:45 */
          });
        }

        /* sections/header.liquid:767 — `<a … id="cart-icon-bubble">`.
           It has no `.shopify-section` descendant, so the live-DOM lookup below
           falls back to the <a> itself, which is what Dawn relies on. */
        if (document.getElementById('cart-icon-bubble')) {
          sections.push({
            id: 'cart-icon-bubble',
            section: 'cart-icon-bubble',
            selector: '.shopify-section'
          });
        }

        /* sections/main-cart-items.liquid:351 — sits inside <form id="cart"> but
           OUTSIDE `.js-contents`, so it survives the items re-render. */
        if (document.getElementById('cart-live-region-text')) {
          sections.push({
            id: 'cart-live-region-text',
            section: 'cart-live-region-text',
            selector: '.shopify-section'
          });
        }

        var cartFooter = document.getElementById('main-cart-footer');
        if (cartFooter && cartFooter.dataset.id) {
          sections.push({
            id: 'main-cart-footer',
            section: cartFooter.dataset.id,
            selector: '.js-contents' /* sections/main-cart-footer.liquid:23 */
          });
        }

        return sections;
      }

      getSectionInnerHTML(html, selector) {
        if (typeof html !== 'string') return null;
        var target;
        try {
          target = new DOMParser()
            .parseFromString(html, 'text/html')
            .querySelector(selector || '.shopify-section');
        } catch (e) {
          return null;
        }
        /* null (not '') so the caller can skip instead of wiping the element —
           e.g. if a merchant removes the `subtotal` block, sections/main-cart-
           footer.liquid renders no `.js-contents` at all. */
        return target ? target.innerHTML : null;
      }

      onChange(event) {
        try {
          var input = event && event.target;
          if (!input || !input.classList || !input.classList.contains('quantity__input')) return;

          /* data-index — sections/main-cart-items.liquid:249,
                           snippets/cart-drawer.liquid:373 */
          var line = input.dataset.index;
          if (!line) return;

          /* "plus" / "minus" — sections/main-cart-items.liquid:227 / :251 */
          var activeElement = document.activeElement;
          var name =
            activeElement && typeof activeElement.getAttribute === 'function'
              ? activeElement.getAttribute('name')
              : null;

          /* data-quantity-variant-id — sections/main-cart-items.liquid:235,
                                        snippets/cart-drawer.liquid:360 */
          this.updateQuantity(line, input.value, name, input.dataset.quantityVariantId);
        } catch (e) {
          /* A throw here must never wedge the cart or any sibling listener. */
          console.error('[cart-items] change handler failed', e);
        }
      }

      /* `.loading-overlay` — sections/main-cart-items.liquid:167 and :289,
         snippets/cart-drawer.liquid:147. Scoped to `this`, as in Dawn. */
      lineLoadingOverlays(line) {
        var overlays = [];
        try {
          overlays = overlays
            .concat(toArray(this.querySelectorAll('#CartItem-' + line + ' .loading-overlay')))
            .concat(toArray(this.querySelectorAll('#CartDrawer-Item-' + line + ' .loading-overlay')));
        } catch (e) {
          /* malformed line id — nothing to toggle */
        }
        return overlays;
      }

      enableLoading(line) {
        var itemsWrapper =
          document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
        if (itemsWrapper) itemsWrapper.classList.add('cart__items--disabled');

        this.lineLoadingOverlays(line).forEach(function (overlay) {
          overlay.classList.remove('hidden');
        });

        if (document.activeElement && typeof document.activeElement.blur === 'function') {
          document.activeElement.blur();
        }

        var status = this.lineItemStatusElement;
        if (status) status.setAttribute('aria-hidden', 'false');
      }

      disableLoading(line) {
        var itemsWrapper =
          document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
        if (itemsWrapper) itemsWrapper.classList.remove('cart__items--disabled');

        this.lineLoadingOverlays(line).forEach(function (overlay) {
          overlay.classList.add('hidden');
        });
      }

      /*
       * `#Line-item-error-{line} .cart-item__error-text`
       *   — sections/main-cart-items.liquid:272-273
       * `#CartDrawer-LineItemError-{line} .cart-item__error-text`
       *   — snippets/cart-drawer.liquid:391, :395
       *
       * Only textContent is written: assets/base.css relies on
       * `.cart-item__error-text:empty + svg { display: none; }` to hide the
       * error icon, so the <small> must never be removed or replaced.
       */
      updateLiveRegions(line, message) {
        var lineItemError =
          document.getElementById('Line-item-error-' + line) ||
          document.getElementById('CartDrawer-LineItemError-' + line);

        if (lineItemError) {
          var errorText = lineItemError.querySelector('.cart-item__error-text');
          if (errorText) errorText.textContent = message || '';
        }

        var status = this.lineItemStatusElement;
        if (status) status.setAttribute('aria-hidden', 'true');

        var cartStatus =
          document.getElementById('cart-live-region-text') || /* main-cart-items.liquid:351 */
          document.getElementById('CartDrawer-LiveRegionText'); /* cart-drawer.liquid:473 */
        if (!cartStatus) return;

        cartStatus.setAttribute('aria-hidden', 'false');
        setTimeout(function () {
          cartStatus.setAttribute('aria-hidden', 'true');
        }, 1000);
      }

      updateQuantity(line, quantity, name, variantId) {
        var self = this;
        var routes = themeRoutes();
        var strings = themeCartStrings();

        this.enableLoading(line);

        var sectionsToRender = this.getSectionsToRender();
        var body = JSON.stringify({
          line: line,
          quantity: quantity,
          sections: sectionsToRender.map(function (section) {
            return section.section;
          }),
          sections_url: window.location.pathname
        });

        var config = themeFetchConfig();
        config.body = body;

        /* Global `fetch` on purpose — snippets/free-gifts.liquid:915 wraps it
           and re-syncs the free-gift lines on any /cart/change write. */
        fetch(routes.cart_change_url || '/cart/change', config)
          .then(function (response) {
            return response.text();
          })
          .then(function (state) {
            var parsedState = JSON.parse(state);

            /* `#Quantity-{line}`       — sections/main-cart-items.liquid:248
               `#Drawer-quantity-{line}` — snippets/cart-drawer.liquid:372 */
            var quantityElement =
              document.getElementById('Quantity-' + line) ||
              document.getElementById('Drawer-quantity-' + line);

            /* `.cart-item` — sections/main-cart-items.liquid:71,
                              snippets/cart-drawer.liquid:143 */
            var items = document.querySelectorAll('.cart-item');

            if (parsedState.errors) {
              if (quantityElement) quantityElement.value = quantityElement.getAttribute('value');
              self.updateLiveRegions(line, parsedState.errors);
              return;
            }

            var isEmpty = parsedState.item_count === 0;

            /* `is-empty` — sections/main-cart-items.liquid:16,
                            sections/main-cart-footer.liquid:1,
                            snippets/cart-drawer.liquid:29 */
            self.classList.toggle('is-empty', isEmpty);

            var cartFooter = document.getElementById('main-cart-footer');
            if (cartFooter) cartFooter.classList.toggle('is-empty', isEmpty);

            var cartDrawer = document.querySelector('cart-drawer');
            if (cartDrawer) cartDrawer.classList.toggle('is-empty', isEmpty);

            sectionsToRender.forEach(function (section) {
              var host = document.getElementById(section.id);
              if (!host) return;
              var elementToReplace = host.querySelector(section.selector) || host;
              var markup = self.getSectionInnerHTML(
                parsedState.sections ? parsedState.sections[section.section] : null,
                section.selector
              );
              if (markup === null) return;
              elementToReplace.innerHTML = markup;
            });

            /* Quantity-clamped notice (Dawn). window.cartStrings —
               layout/theme.liquid:681-684. */
            var lineIndex = parseInt(line, 10) - 1;
            var updatedItem =
              parsedState.items && lineIndex >= 0 ? parsedState.items[lineIndex] : undefined;
            var updatedValue = updatedItem ? updatedItem.quantity : undefined;

            var message = '';
            if (
              quantityElement &&
              parsedState.items &&
              items.length === parsedState.items.length &&
              updatedValue !== parseInt(quantityElement.value, 10)
            ) {
              if (typeof updatedValue === 'undefined') {
                message = strings.error;
              } else {
                message = String(strings.quantityError).replace('[quantity]', updatedValue);
              }
            }
            self.updateLiveRegions(line, message);

            /*
             * Focus restore.
             *
             * DEVIATION FROM A LITERAL DAWN PORT (documented, deliberate):
             * Dawn uses `document.querySelector('cart-drawer')` here. In this
             * theme settings.cart_type == 'drawer' (config/settings_data.json),
             * so layout/theme.liquid:649-651 renders <cart-drawer> on the cart
             * page too — a literal port would trap keyboard focus inside a
             * CLOSED drawer whenever the shopper changes a quantity on
             * /cart. Scoping to `this.closest('cart-drawer')` keeps the drawer
             * behaviour byte-for-byte and gives the cart page a plain .focus().
             */
            var drawerWrapper = self.closest ? self.closest('cart-drawer') : null;

            var lineItem =
              document.getElementById('CartItem-' + line) ||
              document.getElementById('CartDrawer-Item-' + line);
            var buttonToFocus = null;
            if (lineItem && name) {
              try {
                buttonToFocus = lineItem.querySelector('[name="' + name + '"]');
              } catch (e) {
                buttonToFocus = null;
              }
            }

            if (buttonToFocus) {
              if (!themeTrapFocus(drawerWrapper, buttonToFocus)) buttonToFocus.focus();
            } else if (isEmpty && drawerWrapper) {
              /* `.drawer__inner-empty` — snippets/cart-drawer.liquid:40 */
              themeTrapFocus(
                drawerWrapper.querySelector('.drawer__inner-empty'),
                drawerWrapper.querySelector('a')
              );
            } else if (document.querySelector('.cart-item') && drawerWrapper) {
              /* `.cart-item__name` — sections/main-cart-items.liquid:97,
                                      snippets/cart-drawer.liquid:208 */
              themeTrapFocus(drawerWrapper, document.querySelector('.cart-item__name'));
            }

            themePublish(themePubSubEvents().cartUpdate, {
              source: 'cart-items',
              cartData: parsedState,
              variantId: variantId
            });
          })
          .catch(function (error) {
            console.error('[cart-items] quantity update failed', error);

            toArray(self.querySelectorAll('.loading-overlay')).forEach(function (overlay) {
              overlay.classList.add('hidden');
            });

            /* `#cart-errors` — sections/main-cart-footer.liquid:75
               `#CartDrawer-CartErrors` — snippets/cart-drawer.liquid:478 */
            var errors =
              document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
            if (errors) errors.textContent = themeCartStrings().error;
          })
          .then(function () {
            /* `.finally()` equivalent — kept as `.then()` for ES2019 breadth. */
            self.disableLoading(line);
          });
      }

      /*
       * Re-pull our own markup when someone else changed the cart.
       *
       * Cart page: `${routes.cart_url}?section_id=${#main-cart-items[data-id]}`
       * — the section instance id is `cart-items` (templates/cart.json), so it
       * must be read from the DOM rather than hardcoded.
       *
       * The CART-DRAWER-ITEMS branch belongs to the cart-drawer cluster and is
       * expected to be overridden by `CartDrawerItems`. It is implemented here
       * only so the inherited method can never do the wrong thing.
       */
      onCartUpdate() {
        var self = this;
        var routes = themeRoutes();
        var cartUrl = routes.cart_url || '/cart';

        if (this.tagName === 'CART-DRAWER-ITEMS') {
          return fetch(cartUrl + '?section_id=cart-drawer')
            .then(function (response) {
              return response.text();
            })
            .then(function (responseText) {
              var html = new DOMParser().parseFromString(responseText, 'text/html');

              /* Same three regions snippets/free-gifts.liquid:652-657 swaps. */
              ['cart-drawer-items', '.drawer__header', '.drawer__footer'].forEach(function (selector) {
                var live = document.querySelector(selector);
                var fresh = html.querySelector(selector);
                if (live && fresh) live.innerHTML = fresh.innerHTML;
              });

              var freshDrawer = html.querySelector('cart-drawer');
              var liveDrawer = document.querySelector('cart-drawer');
              if (freshDrawer && liveDrawer) {
                liveDrawer.classList.toggle('is-empty', freshDrawer.classList.contains('is-empty'));
              }
            })
            .catch(function (e) {
              console.error('[cart-drawer-items] cart refresh failed', e);
            });
        }

        var mainCartItems = document.getElementById('main-cart-items');
        if (!mainCartItems || !mainCartItems.dataset.id) return undefined;

        return fetch(cartUrl + '?section_id=' + encodeURIComponent(mainCartItems.dataset.id))
          .then(function (response) {
            return response.text();
          })
          .then(function (responseText) {
            var html = new DOMParser().parseFromString(responseText, 'text/html');
            var source = html.querySelector('cart-items');
            if (!source) return;

            self.innerHTML = source.innerHTML;

            /* Dawn stops at the innerHTML swap. Without this the cart page can
               end up showing an empty item list next to a live checkout button,
               because `#main-cart-footer` is a separate section that this
               request does not return. `is-empty` is the class assets/base.css
               keys `.is-empty .cart__footer { display: none; }` off. */
            var nowEmpty = source.classList.contains('is-empty');
            self.classList.toggle('is-empty', nowEmpty);
            var cartFooter = document.getElementById('main-cart-footer');
            if (cartFooter) cartFooter.classList.toggle('is-empty', nowEmpty);
          })
          .catch(function (e) {
            console.error('[cart-items] cart refresh failed', e);
          });
      }
    };
  }

  /* ---------------------------------------------------------------------- *
   * <quantity-input>  — Dawn assets/global.js `QuantityInput`
   * ---------------------------------------------------------------------- */

  var QuantityInput = function () {};

  if (typeof HTMLElement === 'function') {
    QuantityInput = class QuantityInput extends HTMLElement {
      constructor() {
        super();

        this.quantityUpdateUnsubscriber = undefined;
        this.listenersBound = false;

        /* MUST be a plain, bubbling `change` Event — that is the whole contract
           with <cart-items>'s delegated listener. Not `input`, not a
           CustomEvent. */
        this.changeEvent = new Event('change', { bubbles: true });

        this.input = this.querySelector('input'); /* `.quantity__input` */
        this.bindListeners();
      }

      bindListeners() {
        if (this.listenersBound || !this.input) return;
        this.listenersBound = true;

        var self = this;

        this.input.addEventListener('change', function (event) {
          try {
            self.onInputChange(event);
          } catch (e) {
            console.error('[quantity-input] input change failed', e);
          }
        });

        /* Exactly two buttons at all three render sites:
           `button[name="minus"]` — main-cart-items.liquid:227,
                                    cart-drawer.liquid:345,
                                    main-product.liquid:384
           `button[name="plus"]`  — main-cart-items.liquid:251,
                                    cart-drawer.liquid:375,
                                    main-product.liquid:406 */
        toArray(this.querySelectorAll('button')).forEach(function (button) {
          button.addEventListener('click', function (event) {
            try {
              self.onButtonClick(event);
            } catch (e) {
              /* A throw here must not break add-to-cart on the product page. */
              console.error('[quantity-input] button click failed', e);
            }
          });
        });
      }

      connectedCallback() {
        /* Idempotent: every cart/drawer re-render destroys and re-upgrades
           these elements, so nothing may depend on surviving state. */
        if (!this.input) {
          this.input = this.querySelector('input');
          this.bindListeners();
        }

        /* Paints the initial `disabled` state on the +/- buttons — the Liquid
           never emits it (see _live_ref/mp_product.html). */
        this.validateQtyRules();

        var self = this;
        themeSubscribeWhenReady(
          this,
          themePubSubEvents().quantityUpdate,
          function () {
            try {
              self.validateQtyRules();
            } catch (e) {
              console.error('[quantity-input] quantityUpdate handler failed', e);
            }
          },
          function (unsubscriber) {
            self.quantityUpdateUnsubscriber = unsubscriber;
          }
        );
      }

      disconnectedCallback() {
        if (typeof this.quantityUpdateUnsubscriber === 'function') {
          try {
            this.quantityUpdateUnsubscriber();
          } catch (e) {
            /* no-op */
          }
          this.quantityUpdateUnsubscriber = undefined;
        }
      }

      /* Never talks to the cart itself — the cart write is driven by the
         bubbling `change` that <cart-items> catches. */
      onInputChange() {
        this.validateQtyRules();
      }

      onButtonClick(event) {
        event.preventDefault();
        if (!this.input) return;

        var previousValue = this.input.value;

        /* `event.target.name` stays "plus"/"minus" because assets/base.css sets
           `.quantity__button svg { pointer-events: none; }`. */
        if (event.target.name === 'plus') {
          /* `data-min` only exists on the product-page input
             (sections/main-product.liquid:396); on the cart inputs
             `dataset.min` is undefined, so this branch never fires there and
             stepUp() always runs. That asymmetry is intended. */
          if (
            parseInt(this.input.dataset.min, 10) > parseInt(this.input.step, 10) &&
            parseInt(this.input.value, 10) === 0
          ) {
            this.input.value = this.input.dataset.min;
          } else {
            this.input.stepUp();
          }
        } else {
          this.input.stepDown();
        }

        if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

        if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
          this.input.value = parseInt(this.input.min, 10);
        }
      }

      /* `disabled` on `.quantity__button` — assets/base.css
         `.quantity__button.disabled { cursor: not-allowed; opacity: 0.5; }`.
         Deliberately no `pointer-events: none`: native stepUp/stepDown clamping
         is what actually enforces min/max. */
      validateQtyRules() {
        if (!this.input) return;
        var value = parseInt(this.input.value, 10);

        if (this.input.min) {
          var buttonMinus = this.querySelector('.quantity__button[name="minus"]');
          if (buttonMinus) buttonMinus.classList.toggle('disabled', value <= parseInt(this.input.min, 10));
        }

        /* `max` is conditionally absent — main-cart-items.liquid:242-244,
           cart-drawer.liquid:366-368, main-product.liquid:398-401. */
        if (this.input.max) {
          var buttonPlus = this.querySelector('.quantity__button[name="plus"]');
          if (buttonPlus) buttonPlus.classList.toggle('disabled', value >= parseInt(this.input.max, 10));
        }
      }
    };
  }

  /* ---------------------------------------------------------------------- *
   * Registration
   * ---------------------------------------------------------------------- */

  if (typeof customElements !== 'undefined') {
    if (!customElements.get('cart-items')) customElements.define('cart-items', CartItems);
    if (!customElements.get('quantity-input')) customElements.define('quantity-input', QuantityInput);
  }

  /* Exposed as window properties (not just lexical globals) so the cart-drawer
     cluster can do `class CartDrawerItems extends CartItems` — Dawn's shape —
     whether it reads the bare identifier or `window.CartItems`. */
  if (!window.CartItems) window.CartItems = CartItems;
  if (!window.QuantityInput) window.QuantityInput = QuantityInput;
})();
